import { array, compactFlatten, objectHasKeys, objectWithout } from '@art-suite/art-core-ts';
import {
  SCAN_TEMPLATE_CHILDREN_FIELD_NAMES_BY_DEPTH,
  SCAN_TEMPLATE_PART_TYPES_BY_DEPTH,
  ScanTemplatePart,
  ScanTemplatePartOverride,
  ScanTemplateRoot,
  validateScanTemplateDataStructureRecursionDepth,
} from '../ScanTemplateRoot';
import { getScanDataInMemory, getScanDataInMemoryDelta } from './ScanDataInMemoryFunctions';
import { ScanDataInMemory } from './ScanDataInMemoryModels';

const getScanDataInMemoryForOverride = (scanTemplateId: string, overrideType: string, override: ScanTemplatePartOverride, now?: Date): ScanDataInMemory => {
  const { id, ...rest } = override;
  return getScanDataInMemory({
    scanPartId: override.id,
    scanTemplateId,
    overrideType,
    type: 'overrideTemplate',
    data: rest,
  }, now);
};

const getScanDataInMemoryForScanTemplateRecursively = (
  scanTemplateId: string,
  scanTemplatePart: ScanTemplatePart,
  recursionDepth: number,
  now = new Date(),
): ScanDataInMemory[] => {
  validateScanTemplateDataStructureRecursionDepth(recursionDepth);
  const { overrides, id, updatedAt, createdAt, ...rest } = scanTemplatePart;
  const childrenFieldName = SCAN_TEMPLATE_CHILDREN_FIELD_NAMES_BY_DEPTH[recursionDepth];
  const scanPartType = SCAN_TEMPLATE_PART_TYPES_BY_DEPTH[recursionDepth];
  const childParts: ScanTemplatePart[] | undefined = (scanTemplatePart as any)[childrenFieldName] as ScanTemplatePart[];
  let childrenScanPartIds: string[] | undefined = (childParts || overrides)
    ? compactFlatten([
      childParts?.map(childPart => childPart.id),
      array(overrides, override => override.id),
    ])
    : undefined;

  const additionalScanDataInMemory: ScanDataInMemory[][] = [];
  if (recursionDepth === 0) {
    const scanTemplateRoot = scanTemplatePart as ScanTemplateRoot;
    if (objectHasKeys(scanTemplateRoot.taskTemplatesById)) {
      childrenScanPartIds ||= [];
      Object.values(scanTemplateRoot.taskTemplatesById).forEach(taskTemplate => {
        childrenScanPartIds!.push(taskTemplate.id);
        additionalScanDataInMemory.push(getScanDataInMemoryForScanTemplateRecursively(
          scanTemplateId,
          taskTemplate,
          recursionDepth + 2,
          now,
        ));
      });
    }
  }
  return compactFlatten([
    // scanTemplatePart shallow
    getScanDataInMemory({
      scanPartId: id,
      scanTemplateId,
      type: scanPartType,
      data: childrenFieldName ? objectWithout(rest, childrenFieldName, 'taskTemplatesById') : rest,
      childrenScanPartIds,
    }, now),
    // overrides
    ...array(overrides, (override, overrideType) => getScanDataInMemoryForOverride(scanTemplateId, overrideType, override, now)),
    // children
    ...array(childParts, childPart => getScanDataInMemoryForScanTemplateRecursively(scanTemplateId, childPart, recursionDepth + 1, now)).flat(),
    // additional scan data in memory
    ...additionalScanDataInMemory.flat(),
  ]);
};

/**
 * Pure function: Get new ScanData records for a new or existing ScanTemplate (no database operations)
 * @param scanTemplateId
 * @param scanTemplateRoot
 * @returns
 */
export const getFullScanDataInMemoryFromScanTemplateRoot = (scanTemplateId: string, scanTemplateRoot: ScanTemplateRoot, now?: Date): ScanDataInMemory[] =>
  getScanDataInMemoryForScanTemplateRecursively(scanTemplateId, scanTemplateRoot, 0, now);

/**
 * Pure function: Get set of ScanDataInMemory objects representing:
 * - deleted ScanData records
 * - added ScanData records
 * - changed ScanData records
 * comparing before and after ScanRoots
 * @param scanBefore
 * @param scanAfter
 * @returns
 */
export const getScanDataInMemoryDeltaFromScanTemplateRoots = (
  scanTemplateId: string,
  scanTemplateBefore: ScanTemplateRoot,
  scanTemplateAfter: ScanTemplateRoot,
  now?: Date,
): ScanDataInMemory[] => {
  if (scanTemplateBefore.id !== scanTemplateAfter.id) {
    console.error('Two different scanTemplateIds passed to getScanDataInMemoryDeltaFromScanTemplateRoots (root ids should be the same)', {
      scanTemplateBefore,
      scanTemplateAfter,
    });
    throw new Error(
      'Two different scanTemplateIds passed (scanTemplateBefore.id: ' + scanTemplateBefore.id + ', scanTemplateAfter.id: ' + scanTemplateAfter.id + ')',
    );
  }

  return getScanDataInMemoryDelta(
    getFullScanDataInMemoryFromScanTemplateRoot(scanTemplateId, scanTemplateBefore, now),
    getFullScanDataInMemoryFromScanTemplateRoot(scanTemplateId, scanTemplateAfter, now),
    now,
  );
};
